import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Breadcrumbs, Button, Chip, CircularProgress, ClickAwayListener, Dialog, Divider, Drawer, IconButton,
  InputBase, Link, MenuItem, Paper, Skeleton, Stack, TextField, Tooltip, Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import TagIcon from '@mui/icons-material/Tag';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuthStore } from '@/store/auth';
import { useFetchUsuarioResumen } from '@/shared/actions/erp/tickets-soporte-cliente';
import {
  DocSeccion, useFetchDocumentacion, useFetchDocumentacionAdmin, useMutateDocumentacion,
} from '@/shared/actions/erp/tickets-soporte-cliente/documentacion.actions';

// ---- Brand tokens (alineados al theme real del ERP) -------------------------
const DARK = '#111927';     // neutral.800 (SideNav del ERP)
const INK = '#111927';      // text.primary
const MUTED = '#6C737F';    // text.secondary
const PAGE_BG = '#F8FAFC';
const BORDER = '#E5E7EB';

// Audiencias del manual. Cada una se habilita con su permiso del grupo
// "Base de conocimiento" en Gestión de Accesos → Roles (el backend también
// filtra las secciones por estos permisos; superusuario ve todo).
const ROLES: { key: string; label: string; short: string; permiso: string }[] = [
  { key: 'USUARIO', label: 'Usuario / Asesor', short: 'Usuario', permiso: 'tickets.documentacion_usuario' },
  { key: 'ADMIN', label: 'Supervisor / Responsable de área', short: 'Supervisor', permiso: 'tickets.documentacion_admin' },
  { key: 'ADMIN_TI', label: 'Admin TI', short: 'Admin TI', permiso: 'tickets.documentacion_admin_ti' },
];
const roleLabel = (k?: string) => ROLES.find((r) => r.key === k)?.label || (k === 'GENERAL' ? 'General' : k || '');

// Módulos que cubre el manual. Cada sección pertenece a uno; dentro de cada
// pestaña de rol el índice se agrupa por módulo (las secciones antiguas sin
// el campo cuentan como TICKETS).
const MODULOS: { key: string; label: string }[] = [
  { key: 'TICKETS', label: 'Tickets' },
  { key: 'VISITAS', label: 'Visitas técnicas' },
  { key: 'TRASLADOS', label: 'Traslados' },
];
const modKey = (s: DocSeccion) => ((s.modulo || 'TICKETS') + '').toUpperCase();
const moduloLabel = (k?: string) =>
  MODULOS.find((m) => m.key === ((k || 'TICKETS') + '').toUpperCase())?.label || k || 'Tickets';
const modOrder = (s: DocSeccion) => { const i = MODULOS.findIndex((m) => m.key === modKey(s)); return i < 0 ? MODULOS.length : i; };

// Sanitizado básico (contenido de admins de confianza, permiso-gated).
const sanitize = (html: string) =>
  (html || '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/ on\w+="[^"]*"/gi, '')
    .replace(/ on\w+='[^']*'/gi, '')
    .replace(/javascript:/gi, '');

const CONTENT_CSS = `
.kb-content { color:#374151; font-size:1rem; line-height:1.75; font-family:'Inter',-apple-system,'Segoe UI',sans-serif; max-width:760px; }
.kb-content > *:first-child { margin-top:0; }
.kb-content h3 { font-family:'Plus Jakarta Sans',sans-serif; font-size:1.3rem; font-weight:700; margin:2rem 0 .6rem; color:#111927; }
.kb-content h4 { font-family:'Plus Jakarta Sans',sans-serif; font-size:1.08rem; font-weight:700; margin:1.7rem 0 .55rem; color:#111927; scroll-margin-top:96px; }
.kb-content p { margin:.7rem 0; }
.kb-content a { color:#4f46e5; text-decoration:none; font-weight:500; }
.kb-content a:hover { text-decoration:underline; }
.kb-content ul, .kb-content ol { margin:.6rem 0 1rem 1.3rem; padding:0; }
.kb-content li { margin:.35rem 0; }
.kb-content li::marker { color:#6366F1; }
.kb-content strong { color:#111927; }
.kb-content code { background:#f1f5f9; color:#3730a3; padding:2px 6px; border-radius:6px; font-family:'JetBrains Mono',ui-monospace,monospace; font-size:.85em; }
.kb-content pre { background:#0f172a; color:#e2e8f0; padding:14px 16px; border-radius:10px; overflow:auto; font-family:'JetBrains Mono',ui-monospace,monospace; font-size:.85rem; line-height:1.6; }
.kb-content pre code { background:none; color:inherit; padding:0; }
.kb-content blockquote { margin:1rem 0; padding:.6rem 1rem; border-left:4px solid #c7d2fe; background:#f5f7ff; color:#3730a3; border-radius:0 8px 8px 0; }
.kb-content table { border-collapse:collapse; width:100%; margin:1rem 0; font-size:.92rem; display:block; overflow-x:auto; }
.kb-content th, .kb-content td { border:1px solid #e5e7eb; padding:9px 12px; text-align:left; vertical-align:top; }
.kb-content th { background:#f8fafc; font-weight:600; color:#2f3746; text-transform:uppercase; font-size:.72rem; letter-spacing:.04em; }
.kb-content tbody tr:nth-of-type(even) { background:#fbfcfe; }
.kb-content img, .kb-content svg { max-width:100%; height:auto; }
.kb-content hr { border:none; border-top:1px solid #eef2f6; margin:1.7rem 0; }
.kb-content .nota, .kb-content .tip, .kb-content .warn { position:static !important; inset:auto !important; right:auto !important; bottom:auto !important; top:auto !important; left:auto !important; float:none !important; width:auto !important; display:block; padding:12px 16px; border-radius:10px; margin:1rem 0; font-size:.95rem; border:1px solid transparent; }
.kb-content .nota { background:#eff6ff; border-color:#bfdbfe; color:#1e40af; }
.kb-content .tip { background:#ecfdf5; border-color:#a7f3d0; color:#065f46; }
.kb-content .warn { background:#fffbeb; border-color:#fde68a; color:#92400e; }
mark.kb-hit { background:#fef08a; color:#111927; padding:0 2px; border-radius:3px; }
/* Visuales (secciones animadas/ilustradas): clic o botón "Ampliar" → vista a pantalla completa con zoom */
.kb-content .kb-visual svg, .kb-content .kb-visual img { cursor: zoom-in; transition: box-shadow .2s ease; }
.kb-content .kb-visual svg:hover, .kb-content .kb-visual img:hover { box-shadow: 0 0 0 3px rgba(99,102,241,.35); }
.kb-visual > div > div:first-child { display:flex; align-items:center; gap:8px; }
.kb-zoom-btn { margin-left:auto; display:inline-flex; align-items:center; gap:6px; min-height:28px; padding:3px 10px; border-radius:8px;
  background:rgba(255,255,255,.1); color:#fff; border:1px solid rgba(255,255,255,.22); font:600 .72rem/1 'Inter',sans-serif; cursor:pointer;
  white-space:nowrap; transition: background-color .2s ease; }
.kb-zoom-btn:hover { background:rgba(255,255,255,.2); }
.kb-zoom-btn:focus-visible { outline:2px solid #fff; outline-offset:2px; }
.kb-zoom-btn svg { width:14px; height:14px; }
@media (max-width:600px){ .kb-content { font-size:.96rem; } }
`;
const ZOOM_LEVELS = [1, 1.25, 1.5, 2, 3];
const ZOOM_BTN_HTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
  + '<path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg><span>Ampliar</span>';

// ---- Búsqueda: índice por sección + subtítulo (h4), sin tildes/mayúsculas -----
type SearchEntry = {
  id: string; section: DocSeccion; heading?: string; headingIdx?: number; text: string;
  nTitle: string; nHeading: string; nText: string;
};
const RECENT_KEY = 'kb_tickets_recent';
const EJEMPLOS = ['ticket interno', 'escalar', 'cerrar ticket', 'agendar visita', 'traslado', 'dashboard'];
const norm = (s: string) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const wordsOf = (q: string) => norm(q).split(/\s+/).filter((w) => w.length > 1);
const ACCENTS: Record<string, string> = { a: 'aáàäâ', e: 'eéèëê', i: 'iíìïî', o: 'oóòöô', u: 'uúùüû', n: 'nñ', c: 'cç' };
const wordPattern = (w: string) =>
  norm(w).replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/[aeiounc]/g, (c) => `[${ACCENTS[c]}]`);
const hitRegex = (words: string[]) => (words.length ? new RegExp(`(${words.map(wordPattern).join('|')})`, 'gi') : null);
// Quita el bloque visual (SVG/base64) contando la profundidad de <div>.
const stripKbVisual = (html: string) => {
  const i = html.indexOf('<div class="kb-visual"');
  if (i < 0) return html;
  const re = /<\/?div\b[^>]*>/g; re.lastIndex = i;
  let d = 0, end = html.length, m: RegExpExecArray | null;
  while ((m = re.exec(html))) { if (m[0][1] === '/') d--; else d++; if (d === 0) { end = m.index + m[0].length; break; } }
  return html.slice(0, i) + html.slice(end);
};
const htmlToText = (html: string) => {
  try { return (new DOMParser().parseFromString(html, 'text/html').body.textContent || '').replace(/\s+/g, ' ').trim(); }
  catch { return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim(); }
};
function buildIndex(secciones: DocSeccion[]): SearchEntry[] {
  const out: SearchEntry[] = [];
  secciones.forEach((s) => {
    const parts = stripKbVisual(sanitize(s.contenido || '')).split(/(<h4[^>]*>[\s\S]*?<\/h4>)/i);
    let heading: string | undefined; let hIdx = -1;
    parts.forEach((p) => {
      if (/^<h4/i.test(p)) { heading = htmlToText(p); hIdx++; return; }
      const text = htmlToText(p);
      if (!text && heading === undefined) return;
      out.push({ id: `${s.id_documentacion}:${hIdx}`, section: s, heading, headingIdx: hIdx >= 0 ? hIdx : undefined, text,
        nTitle: norm(s.titulo), nHeading: norm(heading || ''), nText: norm(text) });
    });
  });
  return out;
}
// Todas las palabras deben aparecer (título pesa más que subtítulo, y este más que el cuerpo).
function searchIndex(index: SearchEntry[], q: string, limit: number): SearchEntry[] {
  const words = wordsOf(q);
  if (!words.length) return [];
  const phrase = norm(q).trim();
  const scored: { e: SearchEntry; score: number }[] = [];
  for (const e of index) {
    let score = 0, ok = true;
    for (const w of words) {
      const t = e.nTitle.includes(w), h = e.nHeading.includes(w), b = e.nText.includes(w);
      if (!t && !h && !b) { ok = false; break; }
      score += (t ? 8 : 0) + (h ? 5 : 0) + (b ? 1 : 0);
    }
    if (!ok) continue;
    if (phrase.includes(' ') && (e.nText.includes(phrase) || e.nHeading.includes(phrase) || e.nTitle.includes(phrase))) score += 4;
    scored.push({ e, score });
  }
  scored.sort((a, b) => b.score - a.score || (a.e.section.orden ?? 0) - (b.e.section.orden ?? 0));
  return scored.slice(0, limit).map((x) => x.e);
}
function makeSnippet(text: string, words: string[], len = 150) {
  const re = hitRegex(words);
  const m = re ? re.exec(text) : null;
  if (!m) return text.slice(0, len) + (text.length > len ? '…' : '');
  const start = Math.max(0, m.index - Math.floor(len / 3));
  return (start > 0 ? '…' : '') + text.slice(start, start + len) + (start + len < text.length ? '…' : '');
}
// Resalta las coincidencias dentro del HTML de la sección (solo texto; nunca dentro del bloque visual/SVG).
function highlightHtml(html: string, words: string[]) {
  const re = hitRegex(words);
  if (!re) return html;
  let start = 0;
  const i = html.indexOf('<div class="kb-visual"');
  if (i >= 0) {
    const r = /<\/?div\b[^>]*>/g; r.lastIndex = i;
    let d = 0, m: RegExpExecArray | null;
    while ((m = r.exec(html))) { if (m[0][1] === '/') d--; else d++; if (d === 0) { start = m.index + m[0].length; break; } }
  }
  let skip = 0;
  const rest = html.slice(start).split(/(<[^>]+>)/).map((c) => {
    if (c.startsWith('<')) {
      if (/^<(svg|style|script)\b/i.test(c)) skip++; else if (/^<\/(svg|style|script)/i.test(c)) skip = Math.max(0, skip - 1);
      return c;
    }
    return skip || !c.trim() ? c : c.replace(re, '<mark class="kb-hit">$1</mark>');
  });
  return html.slice(0, start) + rest.join('');
}
function useDebounced<T>(v: T, ms: number) {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
}

export default function TicketsKnowledgeBasePage() {
  const theme = useTheme();
  const PRIMARY = theme.palette.primary.main; // #6366F1
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));

  const user = useAuthStore((s) => s.user);
  const isSuper = !!(user as any)?.is_superuser;
  const currentUserId = (user as any)?.user_id || (user as any)?.user || 0;
  const { data: resumenRes, isFetched: permisosCargados } = useFetchUsuarioResumen(currentUserId, !!currentUserId);
  const _r = resumenRes as any;
  const permisos: string[] = _r?.data?.data?.permisos || _r?.data?.permisos || _r?.permisos || [];
  const permisosKey = permisos.join(',');
  const canEdit = isSuper || permisos.includes('tickets.editar_documentacion');
  // Pestañas que el usuario puede ver según sus permisos de "Base de conocimiento".
  const rolesVisibles = useMemo(
    () => ROLES.filter((r) => isSuper || permisos.includes(r.permiso)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSuper, permisosKey]
  );
  const sinAcceso = permisosCargados && !isSuper && rolesVisibles.length === 0 && !canEdit;

  const [rolActivo, setRolActivo] = useState('USUARIO');
  // Si la pestaña activa no está permitida, saltar a la primera visible.
  useEffect(() => {
    if (rolesVisibles.length && !rolesVisibles.some((r) => r.key === rolActivo)) {
      setRolActivo(rolesVisibles[0].key);
    }
  }, [rolesVisibles, rolActivo]);
  const [search, setSearch] = useState('');           // texto del buscador
  const [submitted, setSubmitted] = useState<string | null>(null); // Enter → página de resultados
  const [openSug, setOpenSug] = useState(false);      // dropdown de sugerencias
  const [active, setActive] = useState(0);            // opción activa (teclado)
  const [hl, setHl] = useState<{ words: string[]; headingIdx?: number; nonce: number } | null>(null); // resaltado en la sección
  const [recent, setRecent] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const { data: docsRes, isLoading } = useFetchDocumentacion();
  const seccionesAll: DocSeccion[] = ((docsRes as any)?.data || []) as DocSeccion[];
  // Defensa en el cliente (el backend ya filtra por los mismos permisos).
  const secciones = useMemo(
    () => seccionesAll.filter((s) => s.rol === 'GENERAL' || rolesVisibles.some((r) => r.key === s.rol)),
    [seccionesAll, rolesVisibles]
  );

  const byOrden = (a: DocSeccion, b: DocSeccion) => (a.orden ?? 0) - (b.orden ?? 0);
  const general = useMemo(() => secciones.filter((s) => s.rol === 'GENERAL').sort(byOrden), [secciones]);
  const delRol = useMemo(
    () => secciones.filter((s) => s.rol === rolActivo).sort((a, b) => modOrder(a) - modOrder(b) || byOrden(a, b)),
    [secciones, rolActivo]
  );
  const navOrder = useMemo(() => [...general, ...delRol], [general, delRol]);

  // Deep-linking: al cargar, si el hash apunta a un código de sección, selecciónala.
  useEffect(() => {
    if (!secciones.length) return;
    const hash = decodeURIComponent((window.location.hash || '').replace('#', ''));
    if (!hash) return;
    const found = secciones.find((s) => s.codigo === hash);
    if (found) {
      if (ROLES.some((r) => r.key === found.rol)) setRolActivo(found.rol as string);
      setSelectedId(found.id_documentacion!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secciones.length]);

  // Atajos "/" y Ctrl/Cmd+K para enfocar el buscador.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      const ctrlK = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k';
      if ((e.key === '/' && !typing) || ctrlK) { e.preventDefault(); searchRef.current?.focus(); setOpenSug(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ---- Búsqueda instantánea (dropdown) + página de resultados (Enter) ------
  const index = useMemo(() => buildIndex(secciones), [secciones]);
  const searchDeb = useDebounced(search, 120);
  const words = useMemo(() => wordsOf(search), [search]);
  const sugerencias = useMemo(() => searchIndex(index, searchDeb, 10), [index, searchDeb]);
  const resultados = useMemo(() => (submitted ? searchIndex(index, submitted, 80) : null), [index, submitted]);
  const resultadosPorSeccion = useMemo(() => {
    if (!resultados) return null;
    const groups: { section: DocSeccion; entries: SearchEntry[] }[] = [];
    resultados.forEach((e) => {
      const g = groups.find((x) => x.section.id_documentacion === e.section.id_documentacion);
      if (g) g.entries.push(e); else groups.push({ section: e.section, entries: [e] });
    });
    return groups;
  }, [resultados]);
  useEffect(() => { setActive(0); }, [sugerencias]);

  const pushRecent = (q: string) => {
    const t = q.trim();
    if (!t) return;
    setRecent((r) => {
      const n = [t, ...r.filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, 6);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(n)); } catch { /* sin storage */ }
      return n;
    });
  };
  const clearRecent = () => { setRecent([]); try { localStorage.removeItem(RECENT_KEY); } catch { /* sin storage */ } };
  const clearSearch = () => { setSearch(''); setSubmitted(null); setOpenSug(false); };
  // Abre la sección del resultado, resalta las coincidencias y baja hasta el subtítulo.
  const goToEntry = (e: SearchEntry, q: string) => {
    pushRecent(q);
    if (ROLES.some((r) => r.key === e.section.rol)) setRolActivo(e.section.rol as string);
    setSelectedId(e.section.id_documentacion!);
    setHl({ words: wordsOf(q), headingIdx: e.headingIdx, nonce: Date.now() });
    setSearch(''); setSubmitted(null); setOpenSug(false); setTocOpen(false);
    if (e.section.codigo) window.history.replaceState(null, '', `#${e.section.codigo}`);
    searchRef.current?.blur();
  };
  const submitSearch = (q = search) => {
    const t = q.trim();
    if (!t) return;
    pushRecent(t); setSearch(t); setSubmitted(t); setOpenSug(false); setHl(null);
    searchRef.current?.blur();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpenSug(true); setActive((a) => Math.min(a + 1, Math.max(0, sugerencias.length - 1))); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (openSug && search.trim() && sugerencias[active]) goToEntry(sugerencias[active], search); else submitSearch(); }
    else if (e.key === 'Escape') { e.preventDefault(); if (openSug) setOpenSug(false); else clearSearch(); }
  };
  // Tras abrir desde el buscador: scroll al subtítulo (o a la primera coincidencia).
  useEffect(() => {
    if (!hl) return;
    const t = setTimeout(() => {
      const el = (hl.headingIdx != null ? document.getElementById(`kbh-${hl.headingIdx}`) : null)
        || document.querySelector('.kb-content mark.kb-hit');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); else window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 80);
    return () => clearTimeout(t);
  }, [hl]);

  const seccionActual: DocSeccion | undefined =
    (selectedId && secciones.find((s) => s.id_documentacion === selectedId)) || delRol[0] || general[0];

  // Inyecta ids en los <h4> del contenido → índice "En esta página".
  const { html, headings } = useMemo(() => {
    if (!seccionActual) return { html: '', headings: [] as { id: string; text: string }[] };
    const clean = sanitize(seccionActual.contenido);
    const hs: { id: string; text: string }[] = [];
    let i = 0;
    const withIds = clean.replace(/<h4([^>]*)>([\s\S]*?)<\/h4>/gi, (_m, attrs, inner) => {
      const id = `kbh-${i++}`;
      const text = String(inner).replace(/<[^>]+>/g, '').trim();
      if (text) hs.push({ id, text });
      return `<h4 id="${id}"${attrs}>${inner}</h4>`;
    });
    // Coincidencias resaltadas cuando se llega desde el buscador.
    return { html: hl && hl.words.length ? highlightHtml(withIds, hl.words) : withIds, headings: hs };
  }, [seccionActual, hl]);

  // ---- Zoom de visuales (pantalla completa) ---------------------------------
  const contentRef = useRef<HTMLDivElement>(null);
  // El visual se renderiza con React (HTML del svg/img) — no con refs: el Dialog monta su
  // contenido en un portal un render después y un efecto sobre el ref lo perdería.
  const [zoom, setZoom] = useState<{ html: string; title: string; aspect: number } | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [, setViewportTick] = useState(0);

  // Botón "Ampliar" en la cabecera de cada tarjeta visual (el HTML viene del backend).
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    root.querySelectorAll<HTMLElement>('.kb-visual > div').forEach((card) => {
      const bar = card.firstElementChild as HTMLElement | null;
      if (!bar || !card.querySelector('svg, img') || bar.querySelector('.kb-zoom-btn')) return;
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'kb-zoom-btn'; b.setAttribute('aria-label', 'Ampliar imagen');
      b.innerHTML = ZOOM_BTN_HTML;
      bar.appendChild(b);
    });
  }, [html]);

  const openZoom = (el: Element, title: string) => {
    let aspect = 16 / 9;
    if (el instanceof SVGSVGElement) {
      const vb = (el.getAttribute('viewBox') || '').split(/[\s,]+/).map(Number);
      if (vb.length === 4 && vb[2] > 0 && vb[3] > 0) aspect = vb[2] / vb[3];
    } else if (el instanceof HTMLImageElement && el.naturalWidth && el.naturalHeight) {
      aspect = el.naturalWidth / el.naturalHeight;
    } else {
      const r = el.getBoundingClientRect();
      if (r.width && r.height) aspect = r.width / r.height;
    }
    setZoomLevel(1);
    setZoom({ html: el.outerHTML, title, aspect });
  };
  const closeZoom = () => setZoom(null);
  // Clic en la imagen o en "Ampliar" (delegado: el contenido es HTML inyectado).
  const onContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const t = e.target as HTMLElement;
    const btn = t.closest('.kb-zoom-btn');
    const vis = btn
      ? btn.closest('.kb-visual > div')?.querySelector('svg, img')
      : t.closest('.kb-visual svg, .kb-visual img');
    if (!vis) return;
    e.preventDefault();
    const card = vis.closest('.kb-visual > div');
    const raw = card?.firstElementChild?.textContent || seccionActual?.titulo || 'Imagen';
    openZoom(vis, raw.replace(/Ampliar\s*$/, '').replace(/\s+/g, ' ').trim());
  };
  // Ancho del visual: 1 = ajustado a la pantalla (limitado por ancho o por alto), luego × nivel de zoom.
  const zoomFitW = zoom
    ? Math.max(280, Math.min(window.innerWidth - 32, (window.innerHeight - 56 - 32) * zoom.aspect))
    : 0;
  useEffect(() => {
    if (!zoom) return;
    const onResize = () => setViewportTick((t) => t + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [zoom]);
  const zoomIdx = ZOOM_LEVELS.indexOf(zoomLevel);
  const zoomIn = () => setZoomLevel(ZOOM_LEVELS[Math.min(ZOOM_LEVELS.length - 1, zoomIdx + 1)]);
  const zoomOut = () => setZoomLevel(ZOOM_LEVELS[Math.max(0, zoomIdx - 1)]);
  const onZoomKey = (e: React.KeyboardEvent) => {
    if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomIn(); }
    else if (e.key === '-') { e.preventDefault(); zoomOut(); }
    else if (e.key === '0') { e.preventDefault(); setZoomLevel(1); }
  };

  const selectSection = (s: DocSeccion) => {
    setSelectedId(s.id_documentacion!);
    setSearch(''); setSubmitted(null); setOpenSug(false); setHl(null);
    if (s.codigo) window.history.replaceState(null, '', `#${s.codigo}`);
    setTocOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const changeRole = (key: string) => { setRolActivo(key); setSelectedId(null); setSearch(''); setSubmitted(null); setHl(null); };

  const curIdx = navOrder.findIndex((s) => s.id_documentacion === seccionActual?.id_documentacion);
  const prev = curIdx > 0 ? navOrder[curIdx - 1] : undefined;
  const next = curIdx >= 0 && curIdx < navOrder.length - 1 ? navOrder[curIdx + 1] : undefined;

  if (editMode) return <EditorDocumentacion onClose={() => setEditMode(false)} />;

  // Sin ningún permiso de "Base de conocimiento": aviso claro (los permisos los otorga TI).
  if (sinAcceso) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: PAGE_BG, display: 'grid', placeItems: 'center', p: 2 }}>
        <Paper variant="outlined" sx={{ p: 4, maxWidth: 520, textAlign: 'center', borderColor: BORDER, borderRadius: 3 }}>
          <MenuBookIcon sx={{ fontSize: 48, color: PRIMARY, mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Sin acceso a la base de conocimiento</Typography>
          <Typography variant="body2" sx={{ color: MUTED, mb: 2 }}>
            Tu usuario no tiene habilitada ninguna sección del manual. Solicita el acceso al equipo de TI
            (Gestión de Accesos → Roles → <b>Base de conocimiento</b>).
          </Typography>
          <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => { window.location.href = '/'; }} sx={{ textTransform: 'none' }}>
            Ir al sistema
          </Button>
        </Paper>
      </Box>
    );
  }

  // ---- Sidebar / índice -----------------------------------------------------
  const TocContent = (
    <Box sx={{ p: 1.5 }}>
      {/* Role switcher (segmented) */}
      <Box sx={{ display: 'flex', p: '3px', bgcolor: '#f1f5f9', borderRadius: 2, mb: 1.5 }}>
        {rolesVisibles.map((r) => {
          const active = rolActivo === r.key;
          return (
            <Box
              key={r.key}
              onClick={() => changeRole(r.key)}
              role="tab"
              aria-selected={active}
              sx={{
                flex: 1, textAlign: 'center', py: 0.7, px: 1, borderRadius: 1.5, cursor: 'pointer',
                fontSize: '.8rem', fontWeight: active ? 700 : 500,
                color: active ? '#fff' : MUTED, bgcolor: active ? PRIMARY : 'transparent',
                boxShadow: active ? '0 1px 3px rgba(15,23,42,.18)' : 'none',
                transition: 'all .2s ease',
                '&:hover': { color: active ? '#fff' : INK },
              }}
            >
              {r.short}
            </Box>
          );
        })}
      </Box>

      {general.length > 0 && (
        <>
          <TocGroupLabel>General</TocGroupLabel>
          {general.map((s) => (
            <TocItem key={s.id_documentacion} s={s} active={seccionActual?.id_documentacion === s.id_documentacion}
              primary={PRIMARY} onClick={() => selectSection(s)} />
          ))}
          <Divider sx={{ my: 1.25 }} />
        </>
      )}
      {MODULOS.map((m) => {
        const items = delRol.filter((s) => modKey(s) === m.key);
        if (!items.length) return null;
        return (
          <Box key={m.key} sx={{ mb: 0.75 }}>
            <TocGroupLabel>{m.label}</TocGroupLabel>
            {items.map((s, i) => (
              <TocItem key={s.id_documentacion} s={s} n={i + 1} active={seccionActual?.id_documentacion === s.id_documentacion}
                primary={PRIMARY} onClick={() => selectSection(s)} />
            ))}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: PAGE_BG }}>
      <style>{CONTENT_CSS}</style>

      {/* ===== Header (brand + buscador + acciones) ===== */}
      <Box
        component="header"
        sx={{
          position: 'sticky', top: 0, zIndex: 1200, bgcolor: DARK, color: '#fff',
          px: { xs: 1.5, md: 3 }, minHeight: 64, display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2.5 },
          boxShadow: '0 1px 0 rgba(255,255,255,.06), 0 6px 20px -12px rgba(0,0,0,.5)',
        }}
      >
        {!isMdUp && (
          <IconButton onClick={() => setTocOpen(true)} aria-label="Abrir índice"
            sx={{ color: '#cbd5e1', width: 44, height: 44, '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.08) } }}>
            <MenuIcon />
          </IconButton>
        )}
        <Box sx={{ minWidth: 0, flexShrink: 1, display: { xs: search || openSug ? 'none' : 'block', sm: 'block' } }}>
          <Typography noWrap sx={{ fontWeight: 700, lineHeight: 1.15, fontSize: '1.02rem', fontFamily: "'Plus Jakarta Sans',sans-serif", letterSpacing: '-.01em' }}>
            Base de Conocimiento
          </Typography>
          <Typography noWrap sx={{ color: '#94a3b8', fontSize: '.72rem', display: { xs: 'none', md: 'block' } }}>
            Tickets · Visitas · Traslados — manual por rol
          </Typography>
        </Box>

        {/* Buscador: píldora clara sobre el header oscuro + sugerencias al escribir */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
          <ClickAwayListener onClickAway={() => setOpenSug(false)}>
            <Box sx={{ position: 'relative', width: '100%', maxWidth: 640 }}>
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, height: 44, pl: 1.75, pr: 1, borderRadius: 999,
                  bgcolor: '#fff', color: INK, border: '1px solid transparent',
                  boxShadow: '0 1px 2px rgba(0,0,0,.25)',
                  transition: 'box-shadow .2s ease, border-color .2s ease',
                  '&:focus-within': { borderColor: PRIMARY, boxShadow: `0 0 0 3px ${alpha(PRIMARY, 0.45)}` },
                }}
              >
                <SearchIcon sx={{ fontSize: 20, color: openSug ? PRIMARY : MUTED, transition: 'color .2s', flexShrink: 0 }} />
                <InputBase
                  inputRef={searchRef}
                  value={search}
                  placeholder="Buscar en el manual (p. ej. escalar, visita, traslado)"
                  onChange={(e) => { setSearch(e.target.value); setOpenSug(true); if (submitted) setSubmitted(null); }}
                  onFocus={() => setOpenSug(true)}
                  onKeyDown={onSearchKey}
                  inputProps={{
                    role: 'combobox', 'aria-label': 'Buscar en el manual', 'aria-autocomplete': 'list',
                    'aria-expanded': openSug, 'aria-controls': 'kb-search-listbox',
                    'aria-activedescendant': openSug && search.trim() && sugerencias[active] ? `kb-opt-${active}` : undefined,
                    autoComplete: 'off', spellCheck: false,
                  }}
                  sx={{ flex: 1, minWidth: 0, fontSize: '.92rem', color: INK,
                    '& input': { p: 0, height: 'auto', textOverflow: 'ellipsis' },
                    '& input::placeholder': { color: MUTED, opacity: 1 } }}
                />
                {search && (
                  <IconButton size="small" aria-label="Limpiar búsqueda" onMouseDown={(e) => e.preventDefault()} onClick={clearSearch}
                    sx={{ color: MUTED, width: 32, height: 32, '&:hover': { color: INK } }}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
                <Box component="kbd" aria-hidden sx={{ display: { xs: 'none', md: 'inline-block' }, px: 0.75, border: `1px solid ${BORDER}`,
                  borderRadius: 1, bgcolor: '#f8fafc', color: MUTED, fontSize: '.68rem', lineHeight: 1.8, fontFamily: 'inherit', flexShrink: 0 }}>
                  /
                </Box>
              </Box>

              {openSug && (
                <Paper
                  elevation={0}
                  sx={{
                    position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0, zIndex: 1300,
                    borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: '#fff', color: INK, overflow: 'hidden',
                    boxShadow: '0 24px 48px -12px rgba(16,24,40,.35), 0 4px 12px -4px rgba(16,24,40,.15)',
                    maxHeight: 'min(70vh, 540px)', display: 'flex', flexDirection: 'column',
                  }}
                >
                  {search.trim() ? (
                    words.length === 0 ? (
                      <Typography sx={{ p: 2, textAlign: 'center', color: MUTED, fontSize: '.85rem' }}>
                        Escribe al menos 2 caracteres para buscar.
                      </Typography>
                    ) : sugerencias.length > 0 ? (
                      <>
                        <Box sx={{ px: 1.75, pt: 1.1, pb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography sx={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#94a3b8' }}>
                            Resultados
                          </Typography>
                          <Typography sx={{ fontSize: '.7rem', color: '#94a3b8', display: { xs: 'none', sm: 'block' } }}>
                            ↑↓ navegar · ↵ abrir · Esc cerrar
                          </Typography>
                        </Box>
                        <Box id="kb-search-listbox" role="listbox" aria-label="Resultados del manual" sx={{ overflowY: 'auto', flex: 1, pb: 0.5 }}>
                          {sugerencias.map((e, i) => {
                            const isActive = i === active;
                            return (
                              <Box
                                key={e.id}
                                id={`kb-opt-${i}`}
                                role="option"
                                aria-selected={isActive}
                                onMouseEnter={() => setActive(i)}
                                onMouseDown={(ev) => ev.preventDefault()}
                                onClick={() => goToEntry(e, search)}
                                sx={{
                                  display: 'flex', gap: 1.25, alignItems: 'flex-start', px: 1.5, py: 1, minHeight: 44, cursor: 'pointer',
                                  bgcolor: isActive ? alpha(PRIMARY, 0.08) : 'transparent',
                                  borderLeft: `3px solid ${isActive ? PRIMARY : 'transparent'}`,
                                  transition: 'background-color .15s ease',
                                }}
                              >
                                <Box sx={{ width: 30, height: 30, borderRadius: 1.5, flexShrink: 0, display: 'grid', placeItems: 'center', mt: 0.1,
                                  bgcolor: e.heading ? '#f1f5f9' : alpha(PRIMARY, 0.1), color: e.heading ? MUTED : PRIMARY }}>
                                  {e.heading ? <TagIcon sx={{ fontSize: 17 }} /> : <MenuBookIcon sx={{ fontSize: 17 }} />}
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography noWrap sx={{ fontSize: '.88rem', fontWeight: 600, color: INK, lineHeight: 1.35 }}>
                                    <Highlight text={e.section.titulo} words={words} />
                                    {e.heading && (
                                      <>
                                        <Box component="span" sx={{ color: '#94a3b8', mx: 0.6 }}>›</Box>
                                        <Highlight text={e.heading} words={words} />
                                      </>
                                    )}
                                  </Typography>
                                  {e.text && (
                                    <Typography sx={{ fontSize: '.8rem', color: MUTED, mt: 0.2, lineHeight: 1.45, overflow: 'hidden',
                                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                      <Highlight text={makeSnippet(e.text, words, 140)} words={words} />
                                    </Typography>
                                  )}
                                </Box>
                                <Chip size="small" label={`${ROLES.find((r) => r.key === e.section.rol)?.short || 'General'}${modKey(e.section) !== 'TICKETS' ? ` · ${moduloLabel(e.section.modulo)}` : ''}`}
                                  sx={{ height: 20, fontSize: '.66rem', bgcolor: '#eef2ff', color: '#3730a3', fontWeight: 600, flexShrink: 0,
                                    display: { xs: 'none', sm: 'inline-flex' } }} />
                              </Box>
                            );
                          })}
                        </Box>
                        <Box sx={{ borderTop: `1px solid ${BORDER}`, bgcolor: '#f8fafc', px: 1.5, py: 0.6, display: 'flex',
                          justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography sx={{ fontSize: '.72rem', color: MUTED }}>
                            {sugerencias.length >= 10 ? 'Más de 10 coincidencias' : `${sugerencias.length} coincidencia${sugerencias.length === 1 ? '' : 's'}`}
                          </Typography>
                          <Button size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => submitSearch()}
                            sx={{ textTransform: 'none', fontWeight: 600, minHeight: 32 }}>
                            Ver todos los resultados
                          </Button>
                        </Box>
                      </>
                    ) : searchDeb.trim() !== search.trim() ? (
                      <Typography sx={{ p: 2, textAlign: 'center', color: MUTED, fontSize: '.85rem' }}>Buscando…</Typography>
                    ) : (
                      <NoResults compact q={search} ejemplos={EJEMPLOS} secciones={navOrder.slice(0, 5)}
                        onEjemplo={(t) => submitSearch(t)} onSeccion={selectSection} />
                    )
                  ) : (
                    <Box sx={{ py: 0.5 }}>
                      {recent.length > 0 && (
                        <>
                          <Box sx={{ px: 1.75, pt: 0.75, pb: 0.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#94a3b8' }}>
                              Búsquedas recientes
                            </Typography>
                            <Button size="small" onMouseDown={(e) => e.preventDefault()} onClick={clearRecent}
                              sx={{ textTransform: 'none', fontSize: '.72rem', minHeight: 28, color: MUTED }}>Borrar</Button>
                          </Box>
                          {recent.map((r) => (
                            <Box key={r} role="button" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => submitSearch(r)}
                              sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, minHeight: 40, cursor: 'pointer', fontSize: '.88rem',
                                color: INK, '&:hover': { bgcolor: '#f8fafc' } }}>
                              <HistoryIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                              <span>{r}</span>
                            </Box>
                          ))}
                          <Divider sx={{ my: 0.5 }} />
                        </>
                      )}
                      <Typography sx={{ px: 1.75, pt: 0.75, pb: 0.25, fontSize: '.68rem', fontWeight: 700, letterSpacing: '.08em',
                        textTransform: 'uppercase', color: '#94a3b8' }}>
                        Secciones · {roleLabel(rolActivo)}
                      </Typography>
                      {navOrder.slice(0, 6).map((s) => (
                        <Box key={s.id_documentacion} role="button" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => selectSection(s)}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, minHeight: 40, cursor: 'pointer', fontSize: '.88rem',
                            color: INK, '&:hover': { bgcolor: '#f8fafc' } }}>
                          <MenuBookIcon sx={{ fontSize: 18, color: PRIMARY }} />
                          <span>{s.titulo}</span>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              )}
            </Box>
          </ClickAwayListener>
        </Box>

        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          {canEdit && (
            <Tooltip title="Editar el contenido del manual">
              <Button variant="contained" size="small" startIcon={<EditIcon />} onClick={() => setEditMode(true)}
                sx={{ bgcolor: theme.palette.warning.main, '&:hover': { bgcolor: theme.palette.warning.dark }, minHeight: 36,
                  textTransform: 'none', fontWeight: 600, display: { xs: 'none', sm: 'inline-flex' }, whiteSpace: 'nowrap' }}>
                Editar
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Ir al sistema">
            <IconButton onClick={() => { window.location.href = '/'; }} aria-label="Ir al sistema"
              sx={{ color: '#cbd5e1', width: 44, height: 44, '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.08) } }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* ===== Body ===== */}
      <Box sx={{ maxWidth: 1360, mx: 'auto', display: 'flex', gap: { xs: 0, md: 3 }, p: { xs: 1.5, md: 3 }, alignItems: 'flex-start' }}>
        {/* Left TOC (desktop) */}
        {isMdUp && (
          <Paper variant="outlined" sx={{ width: 280, flexShrink: 0, position: 'sticky', top: 84, borderColor: BORDER, borderRadius: 3,
            maxHeight: 'calc(100vh - 104px)', overflow: 'auto' }}>
            {TocContent}
          </Paper>
        )}
        {/* Left TOC (mobile drawer) */}
        <Drawer open={tocOpen} onClose={() => setTocOpen(false)}>
          <Box sx={{ width: 288 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, bgcolor: DARK, color: '#fff' }}>
              <Typography sx={{ fontWeight: 700 }}>Índice</Typography>
              <IconButton size="small" onClick={() => setTocOpen(false)} sx={{ color: '#cbd5e1' }}><CloseIcon fontSize="small" /></IconButton>
            </Box>
            {TocContent}
          </Box>
        </Drawer>

        {/* Center content */}
        <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: { xs: 2, md: 4 }, borderColor: BORDER, borderRadius: 3 }}>
          {isLoading ? (
            <Stack spacing={1.5}>
              <Skeleton variant="text" width={220} height={40} />
              <Skeleton variant="rounded" height={180} />
              <Skeleton variant="text" width="90%" /><Skeleton variant="text" width="80%" /><Skeleton variant="text" width="85%" />
            </Stack>
          ) : submitted && resultadosPorSeccion ? (
            <>
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Resultados de “{submitted}”
                </Typography>
                <Button size="small" startIcon={<CloseIcon />} onClick={clearSearch} sx={{ textTransform: 'none', color: MUTED }}>
                  Cerrar resultados
                </Button>
              </Stack>
              <Typography variant="body2" sx={{ color: MUTED, mb: 2 }}>
                {resultados?.length || 0} coincidencia{(resultados?.length || 0) === 1 ? '' : 's'} en {resultadosPorSeccion.length} sección{resultadosPorSeccion.length === 1 ? '' : 'es'}
              </Typography>
              {resultadosPorSeccion.length === 0 && (
                <NoResults q={submitted} ejemplos={EJEMPLOS} secciones={navOrder.slice(0, 8)}
                  onEjemplo={(t) => submitSearch(t)} onSeccion={selectSection} />
              )}
              <Stack spacing={1.25}>
                {resultadosPorSeccion.map((g) => {
                  const qw = wordsOf(submitted);
                  return (
                    <Paper key={g.section.id_documentacion} variant="outlined" sx={{ p: 1.5, borderColor: BORDER, borderRadius: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                        <Typography
                          component="button"
                          onClick={() => goToEntry(g.entries[0], submitted)}
                          sx={{ fontWeight: 700, color: INK, fontSize: '1rem', background: 'none', border: 0, p: 0, cursor: 'pointer',
                            textAlign: 'left', fontFamily: 'inherit', '&:hover': { color: PRIMARY } }}
                        >
                          <Highlight text={g.section.titulo} words={qw} />
                        </Typography>
                        <Chip size="small" label={`${roleLabel(g.section.rol as string)}${modKey(g.section) !== 'TICKETS' ? ` · ${moduloLabel(g.section.modulo)}` : ''}`} sx={{ bgcolor: '#eef2ff', color: '#3730a3', fontWeight: 600, height: 22 }} />
                      </Stack>
                      <Stack spacing={0.25}>
                        {g.entries.slice(0, 4).map((e) => (
                          <Box key={e.id} onClick={() => goToEntry(e, submitted)}
                            sx={{ px: 1, py: 0.75, borderRadius: 1.5, cursor: 'pointer', transition: 'background-color .15s ease',
                              '&:hover': { bgcolor: '#f8fafc' } }}>
                            {e.heading && (
                              <Typography sx={{ fontSize: '.88rem', fontWeight: 600, color: '#312e81' }}>
                                <Highlight text={e.heading} words={qw} />
                              </Typography>
                            )}
                            {e.text && (
                              <Typography variant="body2" sx={{ color: MUTED, overflow: 'hidden', display: '-webkit-box',
                                WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                <Highlight text={makeSnippet(e.text, qw, 200)} words={qw} />
                              </Typography>
                            )}
                          </Box>
                        ))}
                        {g.entries.length > 4 && (
                          <Typography variant="caption" sx={{ color: MUTED, px: 1 }}>
                            +{g.entries.length - 4} coincidencia{g.entries.length - 4 === 1 ? '' : 's'} más en esta sección
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </>
          ) : seccionActual ? (
            <>
              <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1.5, fontSize: '.8rem', color: MUTED }}>
                <Typography variant="body2" sx={{ color: MUTED }}>Manual</Typography>
                <Typography variant="body2" sx={{ color: MUTED }}>{roleLabel(seccionActual.rol as string)}</Typography>
                <Typography variant="body2" sx={{ color: MUTED }}>{moduloLabel(seccionActual.modulo)}</Typography>
                <Typography variant="body2" sx={{ color: INK, fontWeight: 600 }}>{seccionActual.titulo}</Typography>
              </Breadcrumbs>

              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2.5 }}>
                <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: alpha(PRIMARY, 0.1), display: 'grid', placeItems: 'center' }}>
                  <MenuBookIcon sx={{ color: PRIMARY, fontSize: 20 }} />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                  {seccionActual.titulo}
                </Typography>
              </Stack>

              <Box ref={contentRef} className="kb-content" onClick={onContentClick} dangerouslySetInnerHTML={{ __html: html }} />

              {/* Prev / Next */}
              <Divider sx={{ mt: 4, mb: 2 }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
                {prev ? (
                  <Paper variant="outlined" onClick={() => selectSection(prev)}
                    sx={{ p: 1.5, flex: 1, cursor: 'pointer', borderColor: BORDER, borderRadius: 2, transition: 'all .2s ease',
                      display: 'flex', alignItems: 'center', gap: 1, '&:hover': { borderColor: PRIMARY, bgcolor: '#f8fafc' } }}>
                    <KeyboardArrowLeftIcon sx={{ color: MUTED }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: MUTED }}>Anterior</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: INK }} noWrap>{prev.titulo}</Typography>
                    </Box>
                  </Paper>
                ) : <Box sx={{ flex: 1 }} />}
                {next ? (
                  <Paper variant="outlined" onClick={() => selectSection(next)}
                    sx={{ p: 1.5, flex: 1, cursor: 'pointer', borderColor: BORDER, borderRadius: 2, transition: 'all .2s ease',
                      display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end', textAlign: 'right',
                      '&:hover': { borderColor: PRIMARY, bgcolor: '#f8fafc' } }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: MUTED }}>Siguiente</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: INK }} noWrap>{next.titulo}</Typography>
                    </Box>
                    <KeyboardArrowRightIcon sx={{ color: MUTED }} />
                  </Paper>
                ) : <Box sx={{ flex: 1 }} />}
              </Stack>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8, color: MUTED }}>
              <MenuBookIcon sx={{ fontSize: 56, opacity: 0.25 }} />
              <Typography sx={{ mt: 1 }}>Aún no hay documentación disponible.</Typography>
            </Box>
          )}
        </Paper>

        {/* Right rail: "En esta página" + ayuda */}
        {isLgUp && !submitted && seccionActual && (
          <Box sx={{ width: 220, flexShrink: 0, position: 'sticky', top: 84 }}>
            {headings.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: '.7rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: MUTED, mb: 1 }}>
                  En esta página
                </Typography>
                <Stack spacing={0.25} sx={{ borderLeft: `2px solid ${BORDER}`, pl: 1.25 }}>
                  {headings.map((h) => (
                    <Link key={h.id} component="button" underline="none"
                      onClick={() => document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                      sx={{ textAlign: 'left', color: MUTED, fontSize: '.82rem', py: 0.25, transition: 'color .2s',
                        '&:hover': { color: PRIMARY } }}>
                      {h.text}
                    </Link>
                  ))}
                </Stack>
              </Box>
            )}
            <Paper variant="outlined" sx={{ p: 1.75, borderColor: BORDER, borderRadius: 3, bgcolor: '#fff' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <HelpOutlineIcon sx={{ color: PRIMARY, fontSize: 20 }} />
                <Typography sx={{ fontWeight: 700, fontSize: '.9rem' }}>¿No lo encuentras?</Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: MUTED, fontSize: '.82rem' }}>
                Usa el buscador (atajo <b>/</b>) o crea un ticket interno al área de Soporte.
              </Typography>
              {canEdit && (
                <Button size="small" startIcon={<EditIcon />} onClick={() => setEditMode(true)}
                  sx={{ mt: 1, textTransform: 'none' }}>Editar esta página</Button>
              )}
            </Paper>
          </Box>
        )}
      </Box>

      <Box sx={{ textAlign: 'center', py: 2, color: MUTED, fontSize: '.75rem' }}>
        EVA · FibraMax — Base de conocimiento: Tickets · Visitas · Traslados
      </Box>

      {/* ===== Visor a pantalla completa (zoom) ===== */}
      <Dialog
        fullScreen
        open={!!zoom}
        onClose={closeZoom}
        onKeyDown={onZoomKey}
        aria-label={zoom ? `Vista ampliada: ${zoom.title}` : 'Vista ampliada'}
        PaperProps={{ sx: { bgcolor: 'rgba(17,25,39,.97)', color: '#fff' } }}
      >
        <Box sx={{ height: 56, px: { xs: 1, md: 2 }, display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(0,0,0,.35)', flexShrink: 0 }}>
          <Typography noWrap sx={{ flex: 1, fontWeight: 600, fontSize: '.92rem', minWidth: 0 }}>{zoom?.title}</Typography>
          <Typography sx={{ display: { xs: 'none', md: 'block' }, color: '#94a3b8', fontSize: '.72rem', mr: 1 }}>
            + / − para el zoom · 0 ajusta · Esc cierra
          </Typography>
          <Tooltip title="Alejar (−)">
            <span>
              <IconButton onClick={zoomOut} disabled={zoomIdx <= 0} aria-label="Alejar" sx={{ color: '#fff', width: 44, height: 44, '&.Mui-disabled': { color: '#475467' } }}>
                <ZoomOutIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Typography sx={{ minWidth: 48, textAlign: 'center', fontSize: '.85rem', fontVariantNumeric: 'tabular-nums' }}>
            {Math.round(zoomLevel * 100)}%
          </Typography>
          <Tooltip title="Acercar (+)">
            <span>
              <IconButton onClick={zoomIn} disabled={zoomIdx >= ZOOM_LEVELS.length - 1} aria-label="Acercar" sx={{ color: '#fff', width: 44, height: 44, '&.Mui-disabled': { color: '#475467' } }}>
                <ZoomInIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Ajustar a la pantalla (0)">
            <span>
              <IconButton onClick={() => setZoomLevel(1)} disabled={zoomLevel === 1} aria-label="Ajustar a la pantalla" sx={{ color: '#fff', width: 44, height: 44, '&.Mui-disabled': { color: '#475467' } }}>
                <FitScreenIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1.25, borderColor: 'rgba(255,255,255,.2)' }} />
          <Tooltip title="Cerrar (Esc)">
            <IconButton onClick={closeZoom} aria-label="Cerrar vista ampliada" sx={{ color: '#fff', width: 44, height: 44 }}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box
          onClick={(e) => { if (e.target === e.currentTarget) closeZoom(); }}
          sx={{ flex: 1, overflow: 'auto', display: 'flex', p: 2 }}
        >
          {/* m:auto centra cuando cabe y permite desplazarse (sin recortar) cuando el zoom lo supera */}
          {zoom && (
            <Box
              key={zoom.title + zoom.html.length}
              sx={{
                width: Math.round(zoomFitW * zoomLevel), flexShrink: 0, m: 'auto',
                '& > svg, & > img': {
                  width: '100% !important', height: 'auto !important', maxWidth: 'none !important', display: 'block',
                  borderRadius: '10px', bgcolor: '#fff', boxShadow: '0 20px 60px rgba(0,0,0,.5)',
                },
              }}
              dangerouslySetInnerHTML={{ __html: zoom.html }}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
}

// ---- helpers / subcomponentes ----------------------------------------------
// Texto con las palabras buscadas resaltadas (sin tildes/mayúsculas).
function Highlight({ text, words }: { text: string; words: string[] }) {
  const re = hitRegex(words);
  if (!re) return <>{text}</>;
  const parts = text.split(re);
  return <>{parts.map((p, i) => (i % 2 === 1 ? <mark key={i} className="kb-hit">{p}</mark> : <span key={i}>{p}</span>))}</>;
}

// Estado "sin resultados" con sugerencias (nunca un callejón sin salida).
function NoResults({ q, ejemplos, secciones, onEjemplo, onSeccion, compact }: {
  q: string; ejemplos: string[]; secciones: DocSeccion[];
  onEjemplo: (t: string) => void; onSeccion: (s: DocSeccion) => void; compact?: boolean;
}) {
  return (
    <Box sx={{ textAlign: 'center', py: compact ? 2.5 : 6, px: 2, color: MUTED }}>
      <SearchIcon sx={{ fontSize: compact ? 30 : 48, opacity: 0.3 }} />
      <Typography sx={{ mt: 0.5, fontWeight: 600, color: INK }}>Sin resultados para “{q}”</Typography>
      <Typography variant="body2" sx={{ mt: 0.5, color: MUTED }}>
        Prueba con una sola palabra clave o revisa la ortografía. Por ejemplo:
      </Typography>
      <Stack direction="row" spacing={0.75} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
        {ejemplos.map((t) => (
          <Chip key={t} label={t} size="small" clickable onMouseDown={(e) => e.preventDefault()} onClick={() => onEjemplo(t)}
            sx={{ bgcolor: '#eef2ff', color: '#3730a3', fontWeight: 600 }} />
        ))}
      </Stack>
      {secciones.length > 0 && (
        <>
          <Typography variant="body2" sx={{ mt: 2, color: MUTED }}>O ve directo a una sección:</Typography>
          <Stack direction="row" spacing={0.75} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {secciones.map((s) => (
              <Chip key={s.id_documentacion} label={s.titulo} size="small" variant="outlined" clickable
                onMouseDown={(e) => e.preventDefault()} onClick={() => onSeccion(s)} />
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}

function TocGroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ px: 1, fontSize: '.68rem', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.5 }}>
      {children}
    </Typography>
  );
}

function TocItem({ s, n, active, primary, onClick }: { s: DocSeccion; n?: number; active: boolean; primary: string; onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 1, py: 0.75, mb: 0.25, borderRadius: 1.5, cursor: 'pointer', fontSize: '.86rem',
        display: 'flex', gap: 1, alignItems: 'center', position: 'relative',
        color: active ? '#312e81' : '#374151',
        bgcolor: active ? alpha(primary, 0.1) : 'transparent',
        fontWeight: active ? 700 : 500,
        transition: 'all .18s ease',
        '&:hover': { bgcolor: active ? alpha(primary, 0.14) : '#f1f5f9' },
        '&::before': active ? {
          content: '""', position: 'absolute', left: 0, top: 6, bottom: 6, width: 3, borderRadius: 3, bgcolor: primary,
        } : {},
      }}
    >
      {n != null && (
        <Box component="span" sx={{ fontWeight: 700, opacity: active ? 0.9 : 0.45, minWidth: 18, fontSize: '.78rem', color: active ? primary : 'inherit' }}>
          {String(n).padStart(2, '0')}
        </Box>
      )}
      <span>{s.titulo}</span>
    </Box>
  );
}

// ============================ EDITOR (admins) ============================
function EditorDocumentacion({ onClose }: { onClose: () => void }) {
  const theme = useTheme();
  const PRIMARY = theme.palette.primary.main;
  const { data: adminRes, isLoading } = useFetchDocumentacionAdmin(true);
  const secciones: DocSeccion[] = ((adminRes as any)?.data || []) as DocSeccion[];
  const { create, update, del } = useMutateDocumentacion();

  const empty: DocSeccion = { rol: 'USUARIO', modulo: 'TICKETS', orden: 0, codigo: '', titulo: '', icono: '', contenido: '', activo: true };
  const [form, setForm] = useState<DocSeccion>(empty);
  const isEditing = !!form.id_documentacion;
  const setF = (k: keyof DocSeccion, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const guardar = () => {
    if (!form.titulo?.trim() || !form.codigo?.trim()) return;
    if (isEditing) update.mutate({ id: form.id_documentacion!, data: form }, { onSuccess: () => setForm(empty) });
    else create.mutate(form, { onSuccess: () => setForm(empty) });
  };
  const eliminar = (s: DocSeccion) => {
    if (!s.id_documentacion) return;
    del.mutate(s.id_documentacion, { onSuccess: () => { if (form.id_documentacion === s.id_documentacion) setForm(empty); } });
  };
  const saving = create.isPending || update.isPending;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: PAGE_BG }}>
      <style>{CONTENT_CSS}</style>
      <Box component="header" sx={{ position: 'sticky', top: 0, zIndex: 1200, bgcolor: DARK, color: '#fff', px: { xs: 1.5, md: 3 }, py: 1.25,
        display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: theme.palette.warning.main, display: 'grid', placeItems: 'center' }}>
          <EditIcon sx={{ fontSize: 20 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mr: 'auto' }}>Editar la base de conocimiento</Typography>
        <Button variant="outlined" color="inherit" size="small" startIcon={<VisibilityIcon />} onClick={onClose}
          sx={{ textTransform: 'none', borderColor: alpha('#fff', 0.3) }}>Ver manual</Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, p: { xs: 1.5, md: 3 }, alignItems: 'flex-start', maxWidth: 1500, mx: 'auto' }}>
        <Paper variant="outlined" sx={{ width: 340, flexShrink: 0, p: 1.5, borderColor: BORDER, borderRadius: 3,
          display: { xs: 'none', md: 'block' }, maxHeight: 'calc(100vh - 104px)', overflow: 'auto' }}>
          <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={() => setForm(empty)} sx={{ mb: 1.5, textTransform: 'none' }}>
            Nueva sección
          </Button>
          {isLoading ? <CircularProgress size={22} /> : secciones.map((s) => (
            <Box key={s.id_documentacion} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.6, borderRadius: 1.5,
              bgcolor: form.id_documentacion === s.id_documentacion ? alpha(PRIMARY, 0.1) : 'transparent', '&:hover': { bgcolor: '#f1f5f9' } }}>
              <Box sx={{ flex: 1, cursor: 'pointer', minWidth: 0 }} onClick={() => setForm({ ...s })}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{s.titulo || '(sin título)'}</Typography>
                <Typography variant="caption" sx={{ color: MUTED }}>{roleLabel(s.rol as string)} · {moduloLabel(s.modulo)} · {s.codigo}{s.activo === false ? ' · inactiva' : ''}</Typography>
              </Box>
              <Tooltip title="Eliminar"><IconButton size="small" onClick={() => eliminar(s)}><DeleteIcon fontSize="small" color="error" /></IconButton></Tooltip>
            </Box>
          ))}
        </Paper>

        <Paper variant="outlined" sx={{ flex: 1, p: { xs: 2, md: 3 }, minWidth: 0, borderColor: BORDER, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>{isEditing ? 'Editar sección' : 'Nueva sección'}</Typography>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Rol / audiencia" size="small" value={form.rol} onChange={(e) => setF('rol', e.target.value)} sx={{ minWidth: 200 }}>
                <MenuItem value="GENERAL">General (introducción)</MenuItem>
                <MenuItem value="USUARIO">Usuario / Asesor</MenuItem>
                <MenuItem value="ADMIN">Supervisor / Responsable de área</MenuItem>
                <MenuItem value="ADMIN_TI">Admin TI (detalle técnico)</MenuItem>
              </TextField>
              <TextField select label="Módulo" size="small" value={((form.modulo || 'TICKETS') + '').toUpperCase()} onChange={(e) => setF('modulo', e.target.value)} sx={{ minWidth: 150 }}>
                {MODULOS.map((m) => (<MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>))}
              </TextField>
              <TextField label="Orden" type="number" size="small" value={form.orden} onChange={(e) => setF('orden', Number(e.target.value))} sx={{ width: 110 }} />
              <TextField select label="Activo" size="small" value={form.activo === false ? 'no' : 'si'} onChange={(e) => setF('activo', e.target.value === 'si')} sx={{ width: 130 }}>
                <MenuItem value="si">Sí</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Título" size="small" fullWidth value={form.titulo} onChange={(e) => setF('titulo', e.target.value)} />
              <TextField label="Código (slug único)" size="small" value={form.codigo} onChange={(e) => setF('codigo', e.target.value)} sx={{ minWidth: 240 }} />
            </Stack>
            <Typography variant="subtitle2">Contenido (HTML)</Typography>
            <TextField multiline minRows={12} value={form.contenido} onChange={(e) => setF('contenido', e.target.value)}
              placeholder="<p>Texto…</p> <h4>Subtítulo</h4> <ul><li>…</li></ul> <div class='nota'>Nota</div> <div class='tip'>Tip</div> <div class='warn'>Advertencia</div>"
              sx={{ '& textarea': { fontFamily: 'JetBrains Mono, monospace', fontSize: '.82rem' } }} />
            <Typography variant="subtitle2">Vista previa</Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff', borderColor: BORDER, borderRadius: 2 }}>
              <Box className="kb-content" dangerouslySetInnerHTML={{ __html: sanitize(form.contenido) }} />
            </Paper>
            <Box>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={guardar} disabled={saving || !form.titulo?.trim() || !form.codigo?.trim()} sx={{ textTransform: 'none' }}>
                {saving ? 'Guardando…' : isEditing ? 'Guardar cambios' : 'Crear sección'}
              </Button>
              {isEditing && (<Button sx={{ ml: 1, textTransform: 'none' }} onClick={() => setForm(empty)}>Cancelar</Button>)}
            </Box>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
